// src/pages/Tour/MonthlyTours.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import tourService from '../../services/tour.service';
import Toast from '../../components/Toast/Toast';
import './TourPages.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const TourTypeBadge = ({ type }) => {
  const config = {
    WORKING: { label: 'Working', cls: 'working' },
    LEAVE: { label: 'Leave', cls: 'leave' },
    CAMPAIGNING: { label: 'Campaigning', cls: 'campaign' },
  };
  const { label, cls } = config[type] || { label: type, cls: 'working' };
  return <span className={`badge-working ${cls}`}>{label}</span>;
};

const MonthlyTours = () => {
  const navigate = useNavigate();
  const now = new Date();

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ entries: [], stats: { workingDays: 0, leaveDays: 0, totalKm: 0 } });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [todayTour, setTodayTour] = useState(null);

  // Fetch today's tour for status banner
  useEffect(() => {
    const checkToday = async () => {
      try {
        const response = await tourService.getTodayTour();
        if (response.success && response.data) setTodayTour(response.data);
      } catch (_) { }
    };
    checkToday();
  }, []);

  useEffect(() => {
    fetchMonthlyData();
  }, [selectedYear, selectedMonth]);

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const response = await tourService.getMonthlyTours(selectedYear, selectedMonth);
      if (response.success && response.data) {
        setData(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to fetch monthly tours', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Year options: current year and 2 years back
  const yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const todayStatusBanner = () => {
    if (!todayTour) {
      return (
        <div className="tour-status-banner none">
          <span className="status-dot amber" />
          No tour started today.&nbsp;
          <button
            onClick={() => navigate('/tour/start-km')}
            style={{ background: 'none', border: 'none', color: '#f57f17', fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            Submit Start KM →
          </button>
        </div>
      );
    }
    if (todayTour.status === 'STARTED') {
      return (
        <div className="tour-status-banner started" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="status-dot green" />
            <span>
              Tour in progress — Start KM: <strong>{todayTour.startKm}</strong>
              {todayTour.startDestination && (
                <> &nbsp;|&nbsp; Destination: <strong>{todayTour.startDestination}</strong></>
              )}
            </span>
          </div>
          <button
            onClick={() => navigate('/tour/end-km')}
            style={{
              background: '#2e7d32',
              border: 'none',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '6px 16px',
              borderRadius: 6,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            🏁 End My Tour
          </button>
        </div>
      );
    }
    if (todayTour.status === 'COMPLETED') {
      return (
        <div className="tour-status-banner completed">
          <span className="status-dot blue" />
          ✅ Today's tour completed — <strong>{todayTour.totalKm} km</strong> traveled.
        </div>
      );
    }
    return null;
  };

  return (
    <div className="tour-page-container" style={{ maxWidth: 1000 }}>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="tour-breadcrumb">
        <a href="/dashboard/overview">Dashboard</a>
        <span>›</span>
        <span>Tour</span>
        <span>›</span>
        <span>Monthly View</span>
      </nav>

      {/* Header */}
      <div className="tour-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h4 className="tour-title">Tour Entries</h4>
          <p className="tour-subtitle">Your monthly tour summary</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {/* Only show Start KM button if no tour today */}
          {!todayTour && (
            <button
              className="btn-tour-primary"
              onClick={() => navigate('/tour/start-km')}
              style={{ padding: '9px 18px', fontSize: 13 }}
            >
              + Start KM
            </button>
          )}
          {/* Show End My Tour button only if today's tour is STARTED */}
          {todayTour?.status === 'STARTED' && todayTour?.tourType === 'WORKING' && (
            <button
              className="btn-tour-primary"
              onClick={() => navigate('/tour/end-km')}
              style={{ padding: '9px 18px', fontSize: 13, background: '#c62828' }}
            >
              🏁 End My Tour
            </button>
          )}
        </div>
      </div>

      {/* Today's Status Banner */}
      {todayStatusBanner()}

      {/* Stats Cards */}
      <div className="monthly-stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">🚗</div>
          <div className="stat-info">
            <span className="stat-value">{data.stats.workingDays}</span>
            <span className="stat-label">Working Days</span>
            <span className="stat-sublabel">This Month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon amber">🏖️</div>
          <div className="stat-info">
            <span className="stat-value">{data.stats.leaveDays}</span>
            <span className="stat-label">Leave Days</span>
            <span className="stat-sublabel">This Month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">📍</div>
          <div className="stat-info">
            <span className="stat-value">{data.stats.totalKm}</span>
            <span className="stat-label">Total KM</span>
            <span className="stat-sublabel">Traveled This Month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">📋</div>
          <div className="stat-info">
            <span className="stat-value">{data.entries.length}</span>
            <span className="stat-label">Total Entries</span>
            <span className="stat-sublabel">This Month</span>
          </div>
        </div>
      </div>

      {/* Month Selector */}
      <div className="month-selector">
        <label>Month:</label>
        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(parseInt(e.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>

        <label>Year:</label>
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(parseInt(e.target.value))}
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="tour-table-container">
        {loading ? (
          <div className="tour-loading">Loading tour entries...</div>
        ) : (
          <table className="tour-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Start KM</th>
                <th>End KM</th>
                <th>Total KM</th>
                <th>Transport</th>
                <th>Status</th>
                <th>Visits</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.length > 0 ? (
                data.entries.map(entry => (
                  <tr key={entry.id}>
                    <td>{formatDate(entry.tourDate)}</td>
                    <td><TourTypeBadge type={entry.tourType} /></td>
                    <td>
                      {entry.startKm
                        ? <span className="km-value">{entry.startKm}</span>
                        : <span className="km-pending">—</span>
                      }
                    </td>
                    <td>
                      {entry.endKm
                        ? <span className="km-value">{entry.endKm}</span>
                        : <span className="km-pending">Pending</span>
                      }
                    </td>
                    <td>
                      {entry.totalKm != null
                        ? <span className="km-value">{entry.totalKm} km</span>
                        : <span className="km-pending">—</span>
                      }
                    </td>
                    <td>
                      {entry.modeOfTransport
                        ? entry.modeOfTransport.replace('_', ' ')
                        : '—'
                      }
                    </td>
                    <td>
                      {entry.status === 'COMPLETED' ? (
                        <span className="badge-working working">COMPLETED</span>
                      ) : entry.tourType === 'WORKING' ? (
                        <button
                          className="btn-tour-primary"
                          style={{ padding: '5px 12px', fontSize: 12, background: '#c62828' }}
                          onClick={() => navigate('/tour/end-km')}
                        >
                          🏁 End Tour
                        </button>
                      ) : (
                        <span className="badge-working campaign">{entry.status}</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-tour-secondary"
                        style={{ padding: '5px 12px', fontSize: 12 }}
                        onClick={() => navigate(`/tour/visits/${entry.tourDate.split('T')[0]}`)}
                      >
                        👁 View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="no-data-row">
                  <td colSpan="7">No tour entries found for {MONTHS[selectedMonth - 1]} {selectedYear}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MonthlyTours;